import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, Upload, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getDefaultEmailHeaderHTML, getSimpleEmailHeaderHTML } from '@/utils/emailHeaderTemplate';
import { uploadEmailLogo } from '@/scripts/uploadEmailLogo';

export const EmailHeaderTemplates: React.FC = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [logoUploaded, setLogoUploaded] = useState(false);

  const copyToClipboard = (html: string, templateName: string) => {
    navigator.clipboard.writeText(html);
    toast.success(`${templateName} copied to clipboard!`);
  };

  const handleUploadLogo = async () => {
    setIsUploading(true);
    try {
      const result = await uploadEmailLogo();
      if (result.success) {
        setLogoUploaded(true);
        toast.success('Logo uploaded successfully to email-images bucket!');
      } else {
        toast.error('Failed to upload logo. Please try again.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload logo. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const defaultHeader = getDefaultEmailHeaderHTML();
  const simpleHeader = getSimpleEmailHeaderHTML();

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Email Header Templates</CardTitle>
          <CardDescription>
            Pre-built header templates you can copy and paste into the header editor
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Upload Logo Section */}
          {!logoUploaded && (
            <div className="bg-muted/50 border rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold">Upload Logo First</h4>
                  <p className="text-xs text-muted-foreground">
                    The logo needs to be uploaded to Supabase storage before using the logo header template.
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={handleUploadLogo}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <>Uploading...</>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Logo
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
          
          {logoUploaded && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                <CheckCircle className="h-4 w-4" />
                Logo uploaded successfully! You can now use the logo header template.
              </div>
            </div>
          )}

          {/* Logo Header */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Logo Header (Recommended)</h3>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(defaultHeader, 'Logo Header')}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy HTML
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Full-width centered logo with link to recruitlook.com
            </p>
            <div className="border rounded-md p-4 bg-muted/50">
              <div dangerouslySetInnerHTML={{ __html: defaultHeader }} />
            </div>
          </div>

          {/* Simple Text Header */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Text Header (Fallback)</h3>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(simpleHeader, 'Text Header')}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy HTML
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Simple text-based header with tagline
            </p>
            <div className="border rounded-md p-4 bg-muted/50">
              <div dangerouslySetInnerHTML={{ __html: simpleHeader }} />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
